import { Component, OnInit, OnDestroy, Input} from "@angular/core";
import { DatePipe } from "@angular/common";
import { Router, NavigationStart } from '@angular/router';
import { GridOptions, ColDef, GridApi } from "ag-grid-community";

import { Subscription  } from "rxjs";
import { filter, map, mergeMap } from 'rxjs/operators';

import { FlowInstanceService } from "../../../../flow/services/flow-instance.service";
import { FlowIndex } from "../../../../common/models/flow.dto";
import { AuthService } from "../../../../services/auth.service";
import { UrlComponent } from "../../../../common/components/data-table/columns/url/url.component";
import { RouterLinkRendererComponent } from "../../../../common/components/ag-grid/router-link-renderer.component";
import { ModalInfoComponent } from "../../../../common/components/ag-grid/modal-info.component";
import { DetailsComponent } from "../../../../common/components/data-table/columns/details/details.component";
import { OrganizationService } from "../../../../common/services/organization.service";
import { dateComparator } from "../../../../common/components/data-table/comparators/data-table.comparators";
import { DomainDefinitionService } from "../../../../builder/services/domain-definition.service";
import { DomainValue } from "../../../../models/domain.model";
import { GridFormatterService } from "../../../../common/services/grid-formatter.service";
import { WellService } from "../../../../common/services/well.service";
import { DataViewRequest, GISPointTypes } from "../../../../common/models/well.model";

@Component({
    selector: "ext-7a-transfer-widget",
    templateUrl: "./ext-7a-transfer-widget.component.html",
    styleUrls: ["./ext-7a-transfer-widget.component.scss"],
    standalone: false
})
export class External7ATransferWidgetComponent implements OnInit, OnDestroy {
    @Input() widgetTitle: string;



    inboundColDefs: ColDef[] = [
        {
            headerClass: "wrapped",
            headerName: "Submitted Date",
            headerTooltip: "Submitted Date",
            tooltipValueGetter: this.gridFormatterService.formatDate("submittedDate"),
            field: "submittedDate",
            valueGetter: this.gridFormatterService.formatDate("submittedDate"),
            comparator: dateComparator,
            filter: "agDateColumnFilter",
            filterParams: this.gridFormatterService.defaultDateFilterParams,
        },
        {
            headerClass: "wrapped",
            headerName: "Application Number",
            headerTooltip: "Application Number",
            tooltipField: "flowIdentifier",
            field: "flowIdentifier",
            cellRendererSelector: (params) => {
                if (params.data.state === "Awaiting 7A Transfer" || params.data.state === "Additional Information") {
                    if (params.data.onBehalf) {
                        return {
                            component: ModalInfoComponent,
                            params: {
                                modalText: (data: any) => { return `Application ${data.flowIdentifier} is a paper submission that must be completed by ODNR.`; },
                                text: (data: any) => { return data.flowIdentifier; }
                            }
                        };
                    } else {
                        return {
                            component: RouterLinkRendererComponent,
                            params: {
                                url: (data: any) => { return `/flow/${data.flowId}`; },
                                textField: 'flowIdentifier'
                            }
                        };
                    }
                   
                }
            },
            filter: "agTextColumnFilter",
            filterParams: this.gridFormatterService.defaultTextFilterParams,
        },
        {
            headerClass: "wrapped",
            headerName: "Status",
            headerTooltip: "Status",
            tooltipValueGetter: this.statusFormatter("state"),
            field: "Status",
            valueGetter: this.statusFormatter("state")
        }
    ];

    inboundData: FlowIndex[];
	inboundGridOptions: GridOptions;
	inboundGridApi?: GridApi;
    outboundColDefs: ColDef[] = [
        {
            headerClass: "wrapped",
            cellRenderer: RouterLinkRendererComponent,
            cellRendererParams: {
                openInNewWindow: true,
                textField: "apiNumber",
                url: (data: any) => {
                    return `${this.router.parseUrl(this.router.url).root}dashboard/view/well/${data.apiNumber}`;
                }
            },
            headerName: "API #",
            headerTooltip: "API #",
            tooltipField: "apiNumber",
            field: "apiNumber",
            filter: "agTextColumnFilter",
            filterParams: this.gridFormatterService.defaultTextFilterParams,
        },
        {
            headerClass: "wrapped",
            headerName: "Well Name",
            headerTooltip: "Well Name",
            tooltipField: "wellName",
            field: "wellName",
            filter: "agTextColumnFilter",
            filterParams: this.gridFormatterService.defaultTextFilterParams,
        },
        {
            headerClass: "wrapped",
            headerName: "Well #",
            headerTooltip: "Well #",
            tooltipField: "wellNumber",
            field: "wellNumber",
            filter: "agTextColumnFilter",
            filterParams: this.gridFormatterService.defaultTextFilterParams,
        },
        {
            headerClass: "wrapped",
            headerName: "Well Disposition",
            headerTooltip: "Well Disposition",
            valueGetter: this.formatWellDomainFieldsType('wellDisposition'),
            tooltipValueGetter: this.formatWellDomainFieldsType('wellDisposition'),
            minWidth: 125
        },
        {
            headerClass: "wrapped",
            headerName: "Well Legacy Tracking",
            headerTooltip: "Well Legacy Tracking",
            valueGetter: this.formatWellDomainFieldsType('wellLegacyTracking'),
            tooltipValueGetter: this.formatWellDomainFieldsType('wellLegacyTracking'),
            minWidth: 125
        },
        {
            headerClass: "wrapped",
            headerName: "County",
            headerTooltip: "County",
            tooltipField: "county",
            field: "county",
            filter: "agTextColumnFilter",
            filterParams: this.gridFormatterService.defaultTextFilterParams,
        }
    ];

    outboundData: any[];
	outboundGridOptions: GridOptions;
	outboundGridApi?: GridApi;
    public orgId: string;
    public orgIds: string[] = [];
    subscriptions: Subscription = new Subscription();
    _allWellDispositionDomainValues: DomainValue[];
    _allWellDispositionLegacyTrackingValues: DomainValue[];

    constructor(
        public datePipe: DatePipe,
        public flowInstanceService: FlowInstanceService,
        public authService: AuthService,
        public organizationService: OrganizationService,
        public domainService: DomainDefinitionService,
        public gridFormatterService: GridFormatterService,
        public wellService: WellService,
        private router: Router,
    )
    {
    }

    ngOnDestroy() {
        if (this.subscriptions)
            this.subscriptions.unsubscribe();
    }

    ngOnInit() {
        this.setGridOptions();
        this.refreshData();

        //Fetch the well Disposition domain values
        this.subscriptions.add(this.domainService.getValues("STATUS_DESC").pipe(
        ).subscribe(
            {
                next: (domainValues: DomainValue[]) => {
                    if (domainValues)
                        this._allWellDispositionDomainValues = domainValues;
                },
                error: (error) => { console.log(error); console.log("ERROR Fetching the STATUS_DESC domain."); }
            }
        ));

        //Fetch all well Disposition LegacyTracking domain values
        this.subscriptions.add(this.domainService.getValues("STATUS_REAS").pipe(
        ).subscribe({
            next: (domainValues: DomainValue[]) => {
                if (domainValues)
                    this._allWellDispositionLegacyTrackingValues = domainValues;
            },
            error: (error) => { console.log(error); console.log("ERROR Fetching the STATUS_DESC domain."); }
        }));

        this.router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe((routerEvent: NavigationStart) => {
            this.subscriptions.unsubscribe();
        });
    }

    onTabClicked(): void {
        if (this.inboundGridApi) {
            this.inboundGridApi.sizeColumnsToFit();
        }
        if (this.outboundGridApi) {
            this.outboundGridApi.sizeColumnsToFit();
        }
    }

    refreshData(): void {
        this.showLoadingOverlays(true );
        this.orgId = this.authService.User.organizationId;
        this.orgIds.push(this.authService.User.organizationId);

        this.subscriptions.add(this.organizationService.getLegacyOrgid(this.orgId).subscribe((result: string) => {
            let legacyOrgId = result;
            this.orgIds.push(legacyOrgId);
            this.subscriptions.add(this.flowInstanceService.queryFlowIndices("7ATransferApplications", this.orgId).subscribe((result: FlowIndex[]) => {
                this.inboundData = result;
            }, (error) => {
                console.log(error);
            }));
            let queryParams = new Map<string, string>();
            queryParams.set("@transferorId", this.orgId);
            queryParams.set("@legacyTransferorId", legacyOrgId);
            let outboundFlowIndicies: any[] = [];
            this.subscriptions.add(this.flowInstanceService.queryFlowIndices("7ATransferOutbound", undefined, queryParams)
                .pipe(map((result: any[]) => {
                    outboundFlowIndicies = result;
                    let wellApis: string[] = [];
                    //for each one go through and pull the apis to a unified lied
                    for (let i = 0; i < outboundFlowIndicies.length; i++) {
                        var nonDomesticIds = outboundFlowIndicies[i].nonDomesticAPINumbers;
                        var exemptDomesticIds = outboundFlowIndicies[i].exemptDomesticAPINumbers;
                        var exemptMississipianIds = outboundFlowIndicies[i].exemptMississippianWellsAPINumbers;

                        if (nonDomesticIds) {
                            wellApis = wellApis.concat(nonDomesticIds.split(','));
                        }

                        if (exemptDomesticIds) {
                            wellApis = wellApis.concat(exemptDomesticIds.split(','));
                        }
                        if (exemptMississipianIds) {
                            wellApis = wellApis.concat(exemptMississipianIds.split(','));
                        }
                    }
                    return wellApis;
                }),
                    mergeMap((apis) => {
                        return this.wellService.getTargetedWellData(apis, DataViewRequest.External7AOutboundView);
                    })
                ).subscribe({
                    next: (wellStubResults: any[]) => {
                        let dataRows: any[] = [];
                        if (wellStubResults) {
                        //Run through each well and build their entry
                            for (let i = 0; i < wellStubResults.length; i++) {
                                let matchingFlowIndex = outboundFlowIndicies.filter(a => (a.nonDomesticAPINumbers && a.nonDomesticAPINumbers.includes(wellStubResults[i].id))
                                    || (a.exemptDomesticAPINumbers && a.exemptDomesticAPINumbers.includes(wellStubResults[i].id))
                                    || (a.exemptMississippianWellsAPINumbers && a.exemptMississippianWellsAPINumbers.includes(wellStubResults[i].id)));

                                if (matchingFlowIndex) {
                                    dataRows.push({
                                        apiNumber: wellStubResults[i].id,
                                        wellName: wellStubResults[i].name,
                                        wellNumber: wellStubResults[i].wellNumber,
                                        wellDisposition: wellStubResults[i].disposition,
                                        wellLegacyTracking: wellStubResults[i].legacyTracking,
                                        county: wellStubResults[i].county
                                    });
                                }   
                            }
                        }
                        this.outboundData = dataRows;              
                    }, 
                    error: (error) => {
                        console.log(error);
                    }
            }));
        }, (error) => {
            console.log(error);
        }));
        this.showLoadingOverlays(false);
    }

    setGridOptions(): void {
		this.inboundGridOptions = this.gridFormatterService.BuildDefaultGridOptionsWithColumns(this.inboundColDefs);
		this.inboundGridOptions.onGridReady = (params) => { 
            this.inboundGridApi = params.api; 
            this.inboundGridApi.sizeColumnsToFit();
        };
        this.inboundGridOptions.headerHeight = 50;
		this.outboundGridOptions = this.gridFormatterService.BuildDefaultGridOptionsWithColumns(this.outboundColDefs);
		this.outboundGridOptions.onGridReady = (params) => { 
            this.outboundGridApi = params.api; 
            this.outboundGridApi.sizeColumnsToFit();
        };
        this.outboundGridOptions.headerHeight = 50;
    }

    showLoadingOverlays(show: boolean): void {
        if (this.inboundGridApi) {
            if (show) {
                this.inboundGridApi.setGridOption("rowData", []);
                this.inboundGridApi.setGridOption("loading", true);
            } else {
                this.inboundGridApi.setGridOption("loading", false);
                this.inboundGridApi.sizeColumnsToFit();
            }
        }
        if (this.outboundGridOptions && this.outboundGridApi) {
            if (show) {
                this.outboundGridApi.setGridOption("rowData", []);
                this.outboundGridApi.setGridOption("loading", true);
            } else {
                this.outboundGridApi.setGridOption("loading", false);
                this.outboundGridApi.sizeColumnsToFit();
            }
        }
    }

    statusFormatter(field: string) {
        return (obj: any) => {
            let status = "";
            try {
                const value = obj.data[field];
                switch (value) {
                    case "Awaiting 7A Transfer":
                    case "Additional Information":
                        const workItem = obj.data["currentWorkItemId"];
                        status = workItem != null && workItem != undefined ? "Returned" : "Not Submitted";
                        break;
                    case "Inspection":
                    case "Permitting Review":
                    case "Surety Review":
                    case "Surety Approver":
                    case "FA Retrieval Documented":
                        status = "Pending";
                        break;
                    case "Approved":
                        status = "Approved";
                        break;
                    case "Denied":
                        status = "Denied";
                        break;
                    case "End":
                        if (obj.data["ApprovedDate"] != null) status = "Approved";
                        else if (obj.data["RejectedDate"] != null) status = "Denied";
                        else status = "Completed";
                        break;
                    default:
                        status = "Not Submitted";
                        break;
                }
            } catch { }
            return status;
        }
    }

    formatWellDomainFieldsType(field: string) {
        return (obj) => {
            if (obj.data) {
                var path = "$." + field;
                let fieldValue = this.gridFormatterService.tryGetValue(obj.data, path);
                let domainValues: DomainValue[];
                switch (field) {
                    case 'wellDisposition':
                        domainValues = this._allWellDispositionDomainValues;
                        break;
                    case 'wellLegacyTracking':
                        domainValues = this._allWellDispositionLegacyTrackingValues;
                        break;
                }

                if (domainValues && domainValues.length > 0) {

                    let matchingDomainValue = domainValues.filter(a => a.Code == fieldValue);
                    if (matchingDomainValue.length > 0)
                        return matchingDomainValue[0].Definition;
                    else
                        return fieldValue;
                }
                else {
                    return fieldValue;
                }
            }
            else return "";
        }
    }

}
